import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './MyServices.css';

const STATUS_COLOR = {
  published: '#108a00',
  pending: '#9a6700',
  draft: '#6b7280',
  rejected: '#b42318',
};

const COPY = {
  zh: {
    kicker: '个人工作区',
    title: '我的服务',
    subtitle: '这里只显示你自己发布的服务，不包含找服务页面里的其他服务。',
    post: '发布服务',
    searchPlaceholder: '搜索我的服务名称、分类或描述',
    search: '搜索',
    status: '状态',
    statuses: [
      { value: 'all', label: '全部' },
      { value: 'published', label: '已发布' },
      { value: 'pending', label: '待审核' },
      { value: 'draft', label: '草稿' },
      { value: 'rejected', label: '已拒绝' },
    ],
    resultCount: count => `${count} 个我的服务`,
    loading: '正在加载我的服务...',
    loadError: '我的服务加载失败，请刷新重试',
    emptyTitle: '你还没有发布服务',
    emptyText: '发布服务后，可以在这里管理服务状态和内容。',
    noFiltered: '当前筛选条件下没有服务。',
    view: '查看已发布服务',
    edit: '编辑',
    delete: '删除',
    confirmDelete: '确认删除',
    cancel: '取消',
    deleteError: '服务删除失败，请稍后重试',
    price: '价格',
    area: '地区',
    statusLabels: {
      published: '已发布',
      pending: '待审核',
      draft: '草稿',
      rejected: '已拒绝',
    },
  },
  en: {
    kicker: 'Personal workspace',
    title: 'My services',
    subtitle: 'Only services published by your account appear here. Other listings belong in service discovery.',
    post: 'Post service',
    searchPlaceholder: 'Search my service name, category, or description',
    search: 'Search',
    status: 'Status',
    statuses: [
      { value: 'all', label: 'All' },
      { value: 'published', label: 'Published' },
      { value: 'pending', label: 'Pending review' },
      { value: 'draft', label: 'Draft' },
      { value: 'rejected', label: 'Rejected' },
    ],
    resultCount: count => `${count} of my services`,
    loading: 'Loading your services...',
    loadError: 'Your services could not be loaded. Refresh and try again.',
    emptyTitle: 'You have not posted any services',
    emptyText: 'Published services will appear here for status and content management.',
    noFiltered: 'No services match the current filters.',
    view: 'View published service',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Confirm delete',
    cancel: 'Cancel',
    deleteError: 'Service deletion failed. Please try again.',
    price: 'Price',
    area: 'Area',
    statusLabels: {
      published: 'Published',
      pending: 'Pending review',
      draft: 'Draft',
      rejected: 'Rejected',
    },
  },
};

function formatDate(value, language) {
  if (!value) return '';
  return new Date(value).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function serviceText(service) {
  return [
    service.title,
    service.category,
    service.method,
    service.description,
    service.area,
    service.availability,
    service.price,
  ].filter(Boolean).join(' ').toLowerCase();
}

export default function MyServices() {
  const { language, translatePhrase } = useLanguage();
  const text = COPY[language] || COPY.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [actionError, setActionError] = useState('');
  const status = searchParams.get('status') || 'all';
  const keywordParam = searchParams.get('keyword') || '';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    api.get('/marketplace/services/mine')
      .then(response => {
        if (active) setServices(Array.isArray(response.data.services) ? response.data.services : []);
      })
      .catch(() => {
        if (!active) return;
        setServices([]);
        setLoadError(text.loadError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [text.loadError]);

  const visibleServices = useMemo(() => {
    const normalizedKeyword = keywordParam.trim().toLowerCase();
    return services.filter(service => (
      (status === 'all' || service.status === status)
      && (!normalizedKeyword || serviceText(service).includes(normalizedKeyword))
    ));
  }, [keywordParam, services, status]);

  const updateSearch = event => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set('keyword', keyword.trim());
    else next.delete('keyword');
    setSearchParams(next);
  };

  const updateStatus = event => {
    const next = new URLSearchParams(searchParams);
    if (event.target.value === 'all') next.delete('status');
    else next.set('status', event.target.value);
    setSearchParams(next);
  };

  const deleteService = async service => {
    setActionError('');
    try {
      await api.delete(`/marketplace/services/${service.id}`);
      setServices(current => current.filter(item => item.id !== service.id));
      setDeleteId(null);
    } catch (error) {
      setActionError(error.response?.data?.error || text.deleteError);
    }
  };

  return (
    <div className="my-services-page">
      <div className="container my-services-shell">
        <header className="my-services-hero">
          <div>
            <div className="my-services-kicker">{text.kicker}</div>
            <h1>{text.title}</h1>
            <p>{text.subtitle}</p>
          </div>
          <Link to="/services/new" className="my-services-primary">{text.post}</Link>
        </header>

        <form className="my-services-toolbar" onSubmit={updateSearch}>
          <input
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            placeholder={text.searchPlaceholder}
          />
          <label>
            <span>{text.status}</span>
            <select value={status} onChange={updateStatus}>
              {text.statuses.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <button type="submit">{text.search}</button>
        </form>

        <div className="my-services-result-bar">
          <strong>{text.resultCount(visibleServices.length)}</strong>
        </div>

        {actionError && <div className="my-services-alert">{actionError}</div>}

        {loading ? (
          <div className="my-services-state">{text.loading}</div>
        ) : loadError ? (
          <div className="my-services-state my-services-error">{loadError}</div>
        ) : visibleServices.length === 0 ? (
          <div className="my-services-state">
            <h2>{services.length ? text.noFiltered : text.emptyTitle}</h2>
            <p>{services.length ? text.noFiltered : text.emptyText}</p>
            <Link to="/services/new" className="my-services-primary">{text.post}</Link>
          </div>
        ) : (
          <section className="my-services-list" aria-label={text.title}>
            {visibleServices.map(service => {
              const statusColor = STATUS_COLOR[service.status] || STATUS_COLOR.draft;
              return (
                <article key={service.id} className="my-service-card">
                  <div className="my-service-card-top">
                    <div>
                      <span
                        className="my-service-status"
                        style={{ color: statusColor, background: `${statusColor}18` }}
                      >
                        {text.statusLabels[service.status] || service.status}
                      </span>
                      <span className="my-service-date">{formatDate(service.created_at, language)}</span>
                    </div>
                    {service.status === 'published' && (
                      <Link to={`/services/listing-${service.id}`} className="my-service-view">{text.view}</Link>
                    )}
                  </div>
                  <h2>{translatePhrase(service.title)}</h2>
                  <p>{translatePhrase(service.description)}</p>
                  <div className="my-service-meta">
                    <span>{translatePhrase(service.category)}</span>
                    <span>{translatePhrase(service.method)}</span>
                    <span>{text.price} {service.price || '-'}</span>
                    <span>{text.area} {translatePhrase(service.area) || '-'}</span>
                  </div>
                  <div className="my-service-actions">
                    <Link className="my-services-secondary" to={`/services/new?edit=${service.id}`}>{text.edit}</Link>
                    {deleteId === service.id ? (
                      <>
                        <button className="my-services-danger" type="button" onClick={() => deleteService(service)}>{text.confirmDelete}</button>
                        <button className="my-services-secondary" type="button" onClick={() => setDeleteId(null)}>{text.cancel}</button>
                      </>
                    ) : (
                      <button className="my-services-secondary" type="button" onClick={() => setDeleteId(service.id)}>{text.delete}</button>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
}
